import { useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import {
  ChevronDown,
  ChevronRight,
  Download,
  Crosshair,
  Building2,
  Menu as MenuIcon,
} from 'lucide-react';
import { MARKET_NAMES, resolveSponsor, backingLabel, type SponsorResolution } from '@hc/shared';
import { PanelShell } from '../panel-shell.js';
import { PanelEmptyState } from '../panel-empty-state.js';
import { useCompany } from '../../api/screener-hooks.js';
import { useEntityActivity } from '../../api/hooks.js';
import { ShortlistButton } from '../watchlist-button.js';
import type {
  CompanyDetailResponse,
  CompanyEntity,
  CompanyFinancialPeriod,
} from '../../api/screener-client.js';
import { usePanelEntityId, useGlobalCurrency, type PanelId, type TabId } from '../store.js';
import { formatMoney, formatCount } from '../screener-columns.js';
import { useOpenBoltOn } from '../use-open-bolt-on.js';
import { openDossier } from '../dossier.js';
import { useOpenMethodology } from '../use-open-methodology.js';
import { statutoryFilingsLink } from '../statutory-filings.js';

// Company Overview Panel — standardized cross-market screening panel.
//
// One template, same rows in every market; only values change. Every gap
// renders an em-dash "—" (no "Unknown" / "Not rated", no absence
// explanation). All gating is PER-COMPANY off the row's own provenance —
// never branched on countryCode:
//   • revenue "reported" ⇔ turnover_confidence_p === 'P100' (a model never
//     reaches 100); anything else is "estimated".
//   • Methodology pill shows only when estimated (P !== 'P100').
//   • Financial-history drill gates per-line: revenue trend only if reported;
//     balance-sheet lines whenever filed.
//
// Money is native-currency throughout (metrics + the panel's `financials`
// array are both native — the EUR /entities/:id/financials endpoint is NOT
// used here), so one formatMoney(value, countryCode, displayCurrency) call
// formats everything uniformly.
//
// Reads /companies/:entityId → { entity, metrics, ukEnrichment, frEnrichment,
// groupSize, financials }.

const TAB_IDS = ['overview', 'financials', 'operations', 'signals'] as const;
type TabKey = (typeof TAB_IDS)[number];
// Clinical/diagnosis naming — the tab keys stay (code), only labels change.
const TAB_LABELS: Record<TabKey, string> = {
  overview: 'Identity',
  financials: 'Vitals',
  operations: 'Profile',
  signals: 'Findings',
};

// Non-active statuses that survive the screener's "exclude dead" filter
// (dissolved / struck_off never reach here). Distressed states flag red;
// dormant/suspended a neutral note. Active shows nothing.
const STATUS_CHIP: Record<string, { label: string; tone: ChipTone }> = {
  bankrupt: { label: 'Bankrupt', tone: 'alert' },
  in_administration: { label: 'In administration', tone: 'alert' },
  dormant: { label: 'Dormant', tone: 'neutral' },
  suspended: { label: 'Suspended', tone: 'neutral' },
  merged: { label: 'Merged duplicate', tone: 'neutral' },
};

export function CompanyOverviewPanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  const entityId = usePanelEntityId(tabId, panelId);
  return (
    <PanelShell
      tabId={tabId}
      panelId={panelId}
      // Body owns no padding — the header/tabs/rows supply their own gutters,
      // so the toolbar sits flush under the chrome like the other panels'
      // toolbars (which use PanelShell's flush ToolbarRegion).
      padding="0"
    >
      {entityId ? (
        <PanelBody entityId={entityId} />
      ) : (
        <PanelEmptyState fill icon={<Building2 size={18} />} title="Open a company record">
          Pick any company in <b>Triage</b>, <b>Ward</b> or the map and its full profile lands here
          — financials, Corryk signals, ownership and bolt-on reach.
          <div style={{ marginTop: 'var(--s-3)' }}>
            It follows your selection automatically. Open the{' '}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                background: 'var(--color-accent-100)',
                color: 'var(--accent)',
                borderRadius: 6,
                padding: '1px 6px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                verticalAlign: 'baseline',
              }}
            >
              <MenuIcon size={12} style={{ flexShrink: 0 }} /> menu
            </span>{' '}
            in this panel’s header → <b>Link</b> to choose which channel it tracks, or <b>Off</b> to
            pin it to one company.
          </div>
        </PanelEmptyState>
      )}
    </PanelShell>
  );
}

function PanelBody({ entityId }: { entityId: string }) {
  const { data, isLoading, error } = useCompany(entityId);
  const [tab, setTab] = useState<TabKey>('overview');

  if (isLoading)
    return <div style={{ padding: 'var(--s-3)', color: 'var(--text-tertiary)' }}>Loading…</div>;
  if (error)
    return (
      <div style={{ padding: 'var(--s-3)', color: 'var(--color-negative-500)' }}>
        Failed to load company.
      </div>
    );
  if (!data) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      <Header data={data} entityId={entityId} />
      <Tabs.Root
        value={tab}
        onValueChange={(v) => setTab(v as TabKey)}
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <Tabs.List
          style={{
            display: 'flex',
            gap: 'var(--s-3)',
            padding: '0 var(--s-3)',
            borderBottom: '1px solid var(--hairline)',
            background: 'var(--surface)',
          }}
        >
          {TAB_IDS.map((k) => (
            <Tabs.Trigger
              key={k}
              value={k}
              // Radix only assigns its roving tab stop after the first pointer
              // interaction, so on a fresh load every trigger is tabIndex -1 and
              // the strip is unreachable by keyboard. Seed it from the selected
              // tab; Radix Tabs activates automatically, so selected === focused
              // and this stays in step as the arrow keys move through.
              tabIndex={tab === k ? 0 : -1}
              style={{
                // Small in-panel control (no new text style): same body size
                // and text colours used elsewhere. The ONLY tab signal is the
                // accent underline on the active tab + the active/muted
                // contrast; inactive tabs are de-emphasised so the row reads
                // as "one selected, others not" rather than a header row.
                background: 'transparent',
                border: 'none',
                padding: '7px 0',
                marginBottom: '-1px',
                fontSize: 'var(--t-body)',
                cursor: 'pointer',
                color: tab === k ? 'var(--text-primary)' : 'var(--text-tertiary)',
                fontWeight: tab === k ? 600 : 500,
                borderBottom: tab === k ? '2px solid var(--accent)' : '2px solid transparent',
              }}
            >
              {TAB_LABELS[k]}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 'var(--s-3)' }}>
          <Tabs.Content value="overview">
            <OverviewTab data={data} />
          </Tabs.Content>
          <Tabs.Content value="financials">
            <FinancialsTab data={data} />
          </Tabs.Content>
          <Tabs.Content value="operations">
            <OperationsTab data={data} />
          </Tabs.Content>
          <Tabs.Content value="signals">
            <SignalsTab data={data} />
          </Tabs.Content>
        </div>
      </Tabs.Root>
    </div>
  );
}

// --- header -----------------------------------------------------------

function Header({ data, entityId }: { data: CompanyDetailResponse; entityId: string }) {
  const { entity, metrics } = data;
  const ccy = useGlobalCurrency();
  const statusChip = entity.status ? STATUS_CHIP[entity.status] : undefined;

  const reported = metrics?.turnoverConfidenceP === 'P100';
  const vintage = vintageYear(data);

  return (
    <div
      style={{
        // No bottom border here — the tab strip below carries the only
        // separator (its underline rail). A second line above the tabs would
        // box the tab row and make it read like a table cell.
        // Top padding matches the other panels' toolbars (--s-2), not --s-3.
        padding: 'var(--s-2) var(--s-3) var(--s-3)',
        background: 'var(--surface)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--s-2)',
      }}
    >
      <Toolbar entityId={entityId} data={data} />

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--t-panel-title, 15px)',
            fontWeight: 700,
            color: 'var(--text-primary)',
          }}
        >
          {entity.legalName ?? '—'}
        </h2>
        <Mono style={{ fontSize: 'var(--t-body)', color: 'var(--text-tertiary)' }}>
          {entity.registryId ?? '—'}
        </Mono>
        {statusChip && <Chip tone={statusChip.tone}>{statusChip.label}</Chip>}
      </div>

      {/* Revenue hero */}
      <div
        style={{
          marginTop: 2,
          background: 'var(--menu-surface)',
          border: '1px solid var(--hairline)',
          borderRadius: 5,
          padding: 'var(--s-3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <span
          style={{
            fontSize: 'var(--t-meta)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--text-tertiary)',
            fontWeight: 600,
          }}
        >
          Revenue
        </span>
        <div
          style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--s-2)', flexWrap: 'wrap' }}
        >
          <Mono
            style={{
              fontSize: 'var(--t-panel-title, 15px)',
              fontWeight: 700,
              letterSpacing: '-0.01em',
            }}
          >
            {formatMoney(metrics?.turnoverScreener, entity.countryCode, ccy)}
          </Mono>
          <Chip tone={reported ? 'pos' : 'est'}>{reported ? 'reported' : 'estimated'}</Chip>
          {metrics?.turnoverConfidenceP && <Chip tone="info">{metrics.turnoverConfidenceP}</Chip>}
        </div>
        {vintage && (
          <span style={{ fontSize: 'var(--t-meta)', color: 'var(--text-tertiary)' }}>
            {vintage}
          </span>
        )}
      </div>
    </div>
  );
}

function Toolbar({ entityId, data }: { entityId: string; data: CompanyDetailResponse }) {
  const openBoltOn = useOpenBoltOn();
  const ccy = useGlobalCurrency();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
      <ShortlistButton entityId={entityId} />
      <button
        type="button"
        title="Show the catchment of bolt-on targets near this company"
        style={actBtnStyle(false)}
        onClick={() => openBoltOn(entityId)}
      >
        <Crosshair size={11} /> Catchment
      </button>
      <button
        type="button"
        title="Open a print-ready Target Dossier (save as PDF)"
        style={actBtnStyle(false)}
        onClick={() => {
          void openDossier(data, ccy).then((ok) => {
            if (!ok) window.alert('Allow pop-ups for this site to open the dossier.');
          });
        }}
      >
        <Download size={11} /> Dossier
      </button>
    </div>
  );
}

// --- tabs -------------------------------------------------------------

function OverviewTab({ data }: { data: CompanyDetailResponse }) {
  const { entity, metrics, ukEnrichment } = data;
  const countryName =
    MARKET_NAMES[entity.countryCode as keyof typeof MARKET_NAMES] ?? entity.countryCode;
  const classBasis = ukEnrichment?.primarySic ? `SIC ${ukEnrichment.primarySic}` : null;

  // Controlling owner = the natural person who controls it (privately owned)
  // OR the parent/group entity that does (corporate-owned). One row covers both
  // — a separate "Group" row would be redundant (a group IS the controller).
  const controllingOwner = metrics?.owner75Name ?? metrics?.corporateParent ?? null;
  const sponsor = resolveSponsor(metrics?.corporateParent);

  return (
    <Section>
      <Row label="Sub-segment">
        {metrics?.subsegment ? (
          <>
            {classBasis && <Meta>{classBasis}</Meta>}
            <span>{metrics.subsegment}</span>
          </>
        ) : (
          <Dash />
        )}
      </Row>
      <Row label="Company type">{text(formatCompanyType(entity))}</Row>
      <Row label="Region · Country">
        <span>{entity.region ? `${entity.region} · ${countryName}` : countryName}</span>
      </Row>
      <Row label="Years active">
        {metrics?.yearsActive != null ? <Mono>{metrics.yearsActive}</Mono> : <Dash />}
      </Row>
      <Row label="Ownership type">{text(metrics?.ownershipType)}</Row>
      <Row label="Controlling owner">{text(controllingOwner)}</Row>
      <Row label="Backing">
        <SponsorValue sponsor={sponsor} />
      </Row>
    </Section>
  );
}

// Backing field. Curated financial sponsor → named owner (info chip); curated
// trade/listed/REIT/family owner → owner + type (neutral chip); LBO-structure
// heuristic → unnamed "Likely sponsor-backed"; else em-dash. Confidence and
// sector ride along in the meta line; 'low' is shown as a caveat.
function SponsorValue({ sponsor }: { sponsor: SponsorResolution | null }) {
  if (!sponsor) return <Dash />;
  const tone: ChipTone = sponsor.status === 'sponsor_backed' ? 'info' : 'neutral';
  const detail =
    sponsor.basis === 'lbo_structure'
      ? 'LBO structure'
      : sponsor.confidence && sponsor.confidence !== 'high'
        ? `${sponsor.confidence} confidence`
        : '';
  return (
    <>
      <Chip tone={tone}>{backingLabel(sponsor)}</Chip>
      {sponsor.backer && <span>{sponsor.backer}</span>}
      {detail && <Meta>{detail}</Meta>}
    </>
  );
}

function FinancialsTab({ data }: { data: CompanyDetailResponse }) {
  const { entity, metrics, ukEnrichment, financials } = data;
  const openMethodology = useOpenMethodology();
  const ccy = useGlobalCurrency();
  const money = (v: number | null | undefined) => formatMoney(v, entity.countryCode, ccy);
  const reported = metrics?.turnoverConfidenceP === 'P100';

  const totalAssets = ukEnrichment?.totalAssets ?? financials[0]?.totalAssets ?? null;
  const accountsDate = ukEnrichment?.accountsMadeUpTo ?? financials[0]?.periodEndDate ?? null;
  const overdue = metrics?.accountsOverdue;

  return (
    <Section>
      <Row label="Revenue">
        <Chip tone={reported ? 'pos' : 'est'}>{reported ? 'reported' : 'estimated'}</Chip>
        <Mono>{money(metrics?.turnoverScreener)}</Mono>
      </Row>
      <Row label="Total assets">
        {totalAssets != null ? <Mono>{money(totalAssets)}</Mono> : <Dash />}
      </Row>
      <Row label="Net assets">
        {metrics?.netAssets != null ? (
          <Mono style={metrics.netAssets < 0 ? { color: 'var(--negative)' } : undefined}>
            {money(metrics.netAssets)}
          </Mono>
        ) : (
          <Dash />
        )}
      </Row>
      <Row label="Accounts">
        {accountsDate ? (
          <>
            <Meta>to {monthYear(accountsDate)}</Meta>
            {overdue ? <Chip tone="alert">Overdue</Chip> : <span>Current</span>}
          </>
        ) : (
          <Dash />
        )}
      </Row>
      <Row label="Statutory filings">
        {(() => {
          const link = statutoryFilingsLink(
            entity.countryCode,
            entity.registryId,
            entity.legalName,
          );
          if (!link) return <Dash />;
          return (
            <a
              href={link.url}
              target="_blank"
              rel="noreferrer noopener"
              title={
                link.exact
                  ? `Open this company's record at ${link.register}`
                  : `Open the ${link.register} search — look up ${entity.registryId ?? entity.legalName ?? 'the company'}`
              }
              style={{ color: 'var(--link)', textDecoration: 'none' }}
            >
              {link.register}
              {link.exact ? '' : ' (search)'} ↗
            </a>
          );
        })()}
      </Row>

      <HistoryDrill financials={financials} reported={reported} />

      {!reported && (
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            title="How the revenue estimate is built"
            style={{ ...actBtnStyle(false), background: 'transparent' }}
            onClick={() => openMethodology('revenue')}
          >
            Methodology
          </button>
        </div>
      )}
    </Section>
  );
}

function OperationsTab({ data }: { data: CompanyDetailResponse }) {
  const { entity, metrics, ukEnrichment, frEnrichment, ukProperty, ukLabour } = data;
  const ccy = useGlobalCurrency();
  const beds = ukEnrichment?.cqcCarehomeBeds ?? frEnrichment?.finessBeds ?? null;
  const sites = ukEnrichment?.cqcLocations ?? frEnrichment?.finessFacilityCount ?? null;
  // Annual NHS England dental contract value (NHSBSA, two-key link via practice
  // postcode + CQC). The statutory-payer component of income — never revenue,
  // and it does not move the served turnover figure.
  const nhsDental = ukEnrichment?.nhsDentalContractValue ?? null;
  const nhsDentalN = ukEnrichment?.nhsDentalContracts ?? null;

  const employees = metrics?.employees;
  const empBand = frEnrichment?.employeeBand ?? null;
  const empEstimated = frEnrichment?.employeeBasis?.startsWith('Estimated') ?? false;
  // "Not rated" is a stored sentinel, not real information — we can't tell a
  // genuine no-rating from no-access, so it renders as an em-dash like any gap.
  const qualityRated =
    metrics?.qualityTier != null && metrics.qualityTier.trim().toLowerCase() !== 'not rated';

  return (
    <Section>
      <Row label="Quality tier">
        {qualityRated ? (
          <>
            {metrics?.qualityBasis && <Chip tone="info">{metrics.qualityBasis}</Chip>}
            <span>{metrics?.qualityTier}</span>
          </>
        ) : (
          <Dash />
        )}
      </Row>
      <Row label="Employees">
        {employees != null ? (
          <Mono>{formatCount(employees)}</Mono>
        ) : empBand ? (
          <>
            {empEstimated && <Chip tone="est">est.</Chip>}
            <span>{empBand}</span>
          </>
        ) : (
          <Dash />
        )}
      </Row>
      <Row label="Capacity">
        {beds != null && beds > 0 ? <Mono>{beds.toLocaleString('en-GB')} beds</Mono> : <Dash />}
      </Row>
      <Row label="Sites">
        {sites != null && sites > 0 ? <Mono>{sites.toLocaleString('en-GB')}</Mono> : <Dash />}
      </Row>
      {entity.countryCode === 'GB' && (
        <Row label="NHS dental contract">
          {nhsDental != null && nhsDental > 0 ? (
            <span
              title="Annual NHS England dental contract value, FY2025/26 (NHSBSA). The statutory-payer component of the practice's income — not total revenue; private fees are additional."
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Chip tone="info">public payer</Chip>
              <Mono>{formatMoney(nhsDental, entity.countryCode, ccy)}</Mono>
              {nhsDentalN != null && nhsDentalN > 1 && (
                <span style={{ opacity: 0.7 }}>· {nhsDentalN} contracts</span>
              )}
            </span>
          ) : (
            <Dash />
          )}
        </Row>
      )}
      <LabourRow labour={ukLabour} />
      <PropertyBlock property={ukProperty} country={entity.countryCode} />
    </Section>
  );
}

// --- Property (GB) -----------------------------------------------------

// Who owns the building each site trades from. GB-only by construction: HM Land
// Registry's CCOD is the only bulk corporate property register across the 27
// markets, so this renders nothing at all elsewhere rather than a row of
// em-dashes pretending the question was asked.
//
// THE EVIDENCE TIER IS THE DESIGN. Only 'operator' is a fact — the title
// holder's company number IS this entity's. 'related_party_psc' is a confirmed
// shared controller. The address and name rungs are INFERENCES and wear the
// same `est.` chip the platform uses everywhere for modelled values, because
// rendering an inference identically to a fact would make the tiering
// decorative.
//
// ⚠️ An unconfirmed holder is NEVER labelled independent. 41% of unconfirmed
// edges are a PSC data gap, not a demonstrated absence of a relationship, so
// 'third_party' reads as "no relationship established", not "external landlord,
// confirmed".
const HOLDER_LABEL: Record<string, string> = {
  operator: 'held by this company',
  related_party_psc: 'related party — shared controller',
  related_party_addr: 'related party — shared registered address',
  related_party_name: 'related party — shared name',
  third_party: 'no relationship established',
};
const LANDLORD_TYPE_LABEL: Record<string, string> = {
  reit: 'REIT',
  fund: 'fund',
  propco_investor: 'investor',
  operator_group: "operator's propco",
  social_housing: 'social housing',
  nominee: 'nominee',
  public: 'public',
  charity: 'charity',
  private: 'private',
};

const HOLDER_TONE: Record<string, ChipTone> = {
  operator: 'info',
  related_party_psc: 'info',
  related_party_addr: 'est',
  related_party_name: 'est',
  third_party: 'neutral',
};

// Local care labour market at the company's homes: published authority rates (SfC England, CIW Wales,
// Care Inspectorate Scotland) — a fact about the area, not about the company's own staff.
function LabourRow({ labour }: { labour: CompanyDetailResponse['ukLabour'] }) {
  if (!labour) return null;
  const pct = (v: number | null) => (v == null ? null : `${(v * 100).toFixed(1)}%`);
  const vs = (i: number | null) =>
    i == null ? null : i >= 1.1 ? 'above' : i <= 0.9 ? 'below' : null;
  const vac = vs(labour.vacancyIndex);
  const turn = vs(labour.turnoverIndex);
  const parts = [
    labour.vacancyRate != null && `vacancy ${pct(labour.vacancyRate)}`,
    labour.turnoverRate != null && `turnover ${pct(labour.turnoverRate)}`,
    labour.hourlyPayGbp != null && `£${labour.hourlyPayGbp.toFixed(2)}/h`,
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <Row label="Local care labour">
      <span
        title={`${labour.periodLabel ?? ''} · median of ${labour.homes} homes in ${labour.authorities} authorities`}
        style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}
      >
        <span>{parts.join(' · ')}</span>
        {vac && <Chip tone={vac === 'above' ? 'est' : 'neutral'}>vacancy {vac} national</Chip>}
        {turn && <Chip tone={turn === 'above' ? 'est' : 'neutral'}>turnover {turn} national</Chip>}
      </span>
    </Row>
  );
}

function PropertyBlock({
  property,
  country,
}: {
  property: CompanyDetailResponse['ukProperty'];
  country: string | null;
}) {
  const [open, setOpen] = useState(false);
  if (country !== 'GB' || !property || property.sites.length === 0) return null;
  const { sites, summary } = property;
  // The shortest REMAINING lease is the one that constrains a deal, so it is the figure surfaced rather
  // than an average. Unexpired years (C'Estimate lease profile) when known; else the original term.
  const left = sites.filter((s) => s.leaseYearsLeft != null);
  const terms = sites.filter((s) => s.leaseYears != null);
  const shortestLeft = left.length
    ? left.reduce((a, b) => ((a.leaseYearsLeft ?? 0) <= (b.leaseYearsLeft ?? 0) ? a : b))
    : null;
  const shortestTerm = terms.length
    ? terms.reduce((a, b) => ((a.leaseYears ?? 0) <= (b.leaseYears ?? 0) ? a : b))
    : null;
  const restricted = sites.filter((s) => s.alienationClause === true).length;
  // Landlord groups behind the buildings this company does NOT hold itself (a separate axis from the
  // operator's own group — never merged into it).
  const groups = new Map<string, { type: string | null; n: number }>();
  for (const s of sites) {
    if (!s.landlordGroup || s.holderClass === 'operator') continue;
    const g = groups.get(s.landlordGroup) ?? { type: s.landlordType, n: 0 };
    g.n += 1;
    groups.set(s.landlordGroup, g);
  }
  const topGroups = [...groups.entries()].sort((a, b) => b[1].n - a[1].n);

  return (
    <>
      <Row label="Property titles">
        <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <Mono>{formatCount(summary.titles)}</Mono>
          <span style={{ opacity: 0.7 }}>
            {summary.freehold} freehold · {summary.leasehold} leasehold
          </span>
        </span>
      </Row>
      <Row label="Freehold held by">
        {summary.operatorHeld > 0 && summary.relatedParty === 0 && summary.thirdParty === 0 ? (
          <>
            <Chip tone="info">this company</Chip>
            <span style={{ opacity: 0.7 }}>on its own balance sheet</span>
          </>
        ) : summary.relatedParty > 0 || summary.thirdParty > 0 ? (
          <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {summary.operatorHeld > 0 && <span>{summary.operatorHeld} by this company</span>}
            {summary.relatedParty > 0 && (
              <>
                <Chip tone="est">{summary.relatedParty} related party</Chip>
              </>
            )}
            {summary.thirdParty > 0 && (
              <Chip tone="neutral">{summary.thirdParty} not established</Chip>
            )}
          </span>
        ) : (
          <Dash />
        )}
      </Row>
      {topGroups.length > 0 && (
        <Row label="Landlord groups">
          <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {topGroups.slice(0, 3).map(([name, g]) => (
              <span key={name} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <span>{name}</span>
                {g.type && <Chip tone="neutral">{LANDLORD_TYPE_LABEL[g.type] ?? g.type}</Chip>}
                {g.n > 1 && <Meta>{g.n}</Meta>}
              </span>
            ))}
            {topGroups.length > 3 && <Meta>+{topGroups.length - 3} more</Meta>}
          </span>
        </Row>
      )}
      {summary.noCorporateTitle > 0 && (
        <Row label="No corporate title">
          <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <Mono>{formatCount(summary.noCorporateTitle)}</Mono>
            <Meta>
              {summary.noCorporateTitle === 1 ? 'home' : 'homes'} · individual, trust or
              unregistered
            </Meta>
          </span>
        </Row>
      )}
      {shortestLeft ? (
        <Row label="Shortest lease left">
          <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {(shortestLeft.leaseYearsLeft ?? 0) > 0 ? (
              <Mono>{shortestLeft.leaseYearsLeft} yr</Mono>
            ) : (
              <Chip tone="est">expired on the register</Chip>
            )}
            {restricted > 0 && <Chip tone="est">assignment restricted</Chip>}
          </span>
        </Row>
      ) : (
        shortestTerm && (
          <Row label="Shortest lease term">
            <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <Mono>{shortestTerm.leaseYears} yr</Mono>
              {restricted > 0 && <Chip tone="est">assignment restricted</Chip>}
            </span>
          </Row>
        )
      )}
      <div style={{ marginTop: 4 }}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            font: 'inherit',
            color: 'var(--text-secondary)',
          }}
        >
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          {open ? 'Hide' : 'Show'} {sites.length} {sites.length === 1 ? 'title' : 'titles'}
        </button>
        {open && (
          <div style={{ marginTop: 6, display: 'grid', gap: 6 }}>
            {sites.map((s) => (
              <div
                key={`${s.siteId}-${s.holderCompanyNumber ?? s.holderName ?? ''}`}
                style={{
                  display: 'grid',
                  gap: 2,
                  padding: '6px 8px',
                  border: '1px solid var(--hairline)',
                  borderRadius: 3,
                }}
              >
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong style={{ fontWeight: 600 }}>
                    {s.siteName ?? s.postcode ?? s.siteId}
                  </strong>
                  {s.tenure && <Chip tone="neutral">{s.tenure}</Chip>}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ opacity: 0.75 }}>{s.holderName ?? '—'}</span>
                  {s.holderClass && (
                    <Chip tone={HOLDER_TONE[s.holderClass] ?? 'neutral'}>
                      {HOLDER_LABEL[s.holderClass] ?? s.holderClass}
                    </Chip>
                  )}
                </div>
                {s.holderClass !== 'operator' &&
                  (s.landlordGroup || (s.ultimateOwner && s.ultimateOwner !== s.holderName)) && (
                    <div
                      style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <Meta>owned by</Meta>
                      <span>{s.landlordGroup ?? s.ultimateOwner}</span>
                      {s.landlordType && (
                        <Chip tone="neutral">
                          {LANDLORD_TYPE_LABEL[s.landlordType] ?? s.landlordType}
                        </Chip>
                      )}
                    </div>
                  )}
                {s.addressCorroboration && s.addressCorroboration !== 'corroborated' && (
                  <div>
                    <Chip tone="est">
                      {s.addressCorroboration === 'street_ok_number_differs'
                        ? 'title street matches, number differs'
                        : 'title address not confirmed'}
                    </Chip>
                  </div>
                )}
                {s.leaseTerm && (
                  <div style={{ opacity: 0.6, fontSize: '0.92em' }}>
                    {s.leaseTerm}
                    {s.leaseYearsLeft != null &&
                      (s.leaseYearsLeft > 0 ? ` · ${s.leaseYearsLeft} yr left` : ' · expired')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// --- Financial history drill ------------------------------------------

function HistoryDrill({
  financials,
  reported,
}: {
  financials: CompanyFinancialPeriod[];
  reported: boolean;
}) {
  const [open, setOpen] = useState(false);
  const hist = buildHistory(financials, reported);
  if (!hist) return null;

  return (
    <div style={{ marginTop: 8 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          fontSize: 'var(--t-meta)',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--text-tertiary)',
        }}
      >
        <span style={{ flex: 1, textAlign: 'left' }}>Financial history · reported</span>
        {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
      </button>

      {open && (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 'var(--t-body)',
            marginTop: 8,
          }}
        >
          <thead>
            <tr>
              <th style={histTh('left')} />
              {hist.years.map((y) => (
                <th key={y} style={histTh('right')}>
                  {y}
                </th>
              ))}
              <th style={histTh('right')}>CAGR</th>
            </tr>
          </thead>
          <tbody>
            {hist.lines.map((line) => (
              <tr key={line.label}>
                <td style={histTd('left')}>{line.label}</td>
                {line.cells.map((c, i) => (
                  <td key={i} style={histTd('right')}>
                    {c == null ? '—' : formatCount(c)}
                  </td>
                ))}
                <td
                  style={{
                    ...histTd('right'),
                    background:
                      line.cagr == null
                        ? undefined
                        : line.cagr >= 0
                          ? 'var(--positive-wash)'
                          : 'var(--negative-wash)',
                    color:
                      line.cagr == null
                        ? 'var(--text-tertiary)'
                        : line.cagr >= 0
                          ? 'var(--positive, #0a7d5a)'
                          : 'var(--negative, #c0143c)',
                  }}
                >
                  {line.cagr == null
                    ? '—'
                    : `${line.cagr >= 0 ? '+' : '−'}${Math.round(Math.abs(line.cagr) * 100)}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

interface HistLine {
  label: string;
  cells: (number | null)[];
  cagr: number | null;
}
interface History {
  years: number[];
  lines: HistLine[];
}

// Per-line gating: revenue trends only when reported (P100); balance-sheet
// lines trend whenever they're filed. Needs ≥2 periods with that line.
function buildHistory(financials: CompanyFinancialPeriod[], reported: boolean): History | null {
  const periods = financials.filter((p) => p.periodEndDate).slice(0, 3);
  if (periods.length < 2) return null;
  const years = periods.map((p) => new Date(p.periodEndDate).getFullYear());
  const spanYears = years[0]! - years[years.length - 1]!;

  const lines: HistLine[] = [];
  const addLine = (label: string, key: keyof CompanyFinancialPeriod, gate: boolean) => {
    if (!gate) return;
    const cells = periods.map((p) => (p[key] as number | null) ?? null);
    if (cells.filter((c) => c != null).length < 2) return;
    const newest = cells[0];
    const oldest = cells[cells.length - 1];
    const cagr =
      newest != null && oldest != null && oldest > 0 && spanYears > 0
        ? Math.pow(newest / oldest, 1 / spanYears) - 1
        : null;
    lines.push({ label, cells, cagr });
  };
  addLine('Revenue', 'revenue', reported);
  addLine('Total assets', 'totalAssets', true);
  addLine('Net assets', 'equity', true);
  if (lines.length === 0) return null;
  return { years, lines };
}

// --- Signals tab ------------------------------------------------------
//
// The interpretation layer: fixed rows computed by triangulating data the
// panel already holds — never modelled revenue, never a restatement of
// another tab (Succession, Balance-sheet structure, Revenue trajectory,
// Service quality trajectory). The two peer-relative rows (Sub-scale
// independent, Peer-relative margin) were REMOVED 2026-07-22 (Alex): they
// had shown "pending peer cohort" since Phase A — a permanently-pending row
// reads as a broken panel, not a roadmap. Re-add only WITH the cohort.
// Each row is ALWAYS present; value-vs-n/a is computed per entity.

function SignalsTab({ data }: { data: CompanyDetailResponse }) {
  const { entity, metrics, ukEnrichment, frEnrichment, financials } = data;
  const ccy = useGlobalCurrency();
  const money = (v: number | null | undefined) => formatMoney(v, entity.countryCode, ccy);

  // 1 · Succession — an opportunity (a motivated seller), only meaningful on
  // single-individual control ≥75%. High = owner 65+, Medium = 60–64 OR sole
  // control + a long-established company (approaching), Low otherwise.
  const individualControlled = (metrics?.ownershipType ?? '').toLowerCase().includes('individual');
  const ownerAge = individualControlled
    ? (metrics?.owner75Age ?? frEnrichment?.oldestOwnerAge ?? null)
    : null;
  const ageIsProxy =
    individualControlled && metrics?.owner75Age == null && frEnrichment?.oldestOwnerAge == null;
  const companyAge = metrics?.yearsActive ?? null;
  // Sponsor backing reshapes succession: a PE-held platform is not a
  // founder-succession opportunity. Founder control still takes precedence.
  const sponsor = resolveSponsor(metrics?.corporateParent);
  const succession: { state: string; tone: ChipTone | null; why: string } = (() => {
    if (!individualControlled) {
      if (sponsor) {
        const who = sponsor.backer ?? sponsor.operator;
        if (sponsor.status === 'group_owned')
          return {
            state: 'Group-held',
            tone: 'neutral',
            why: who ? `held by ${who}` : 'part of a wider group',
          };
        return {
          state: 'Sponsor-held',
          tone: 'info',
          why: who ? `held by ${who}` : 'sponsor-backed · LBO structure',
        };
      }
      if (metrics?.ownershipType)
        return { state: 'Low', tone: 'neutral', why: 'no single 75%+ controller' };
      return { state: 'n/a', tone: null, why: 'no ownership data' };
    }
    if (ownerAge != null) {
      if (ownerAge >= 65)
        return { state: 'High', tone: 'est', why: `owner ${ownerAge} · sole 75%+ controller` };
      if (ownerAge >= 60)
        return {
          state: 'Medium',
          tone: 'info',
          why: `owner ${ownerAge} · sole control, approaching`,
        };
      return { state: 'Low', tone: 'neutral', why: `owner ${ownerAge} · sole control` };
    }
    if (companyAge != null && companyAge >= 15)
      return { state: 'Medium', tone: 'info', why: `sole control · ${companyAge}y established` };
    return { state: 'Low', tone: 'neutral', why: 'sole control · younger company' };
  })();

  // 4 · Balance-sheet structure — equity ratio off the FILED balance sheet
  // (net assets ÷ total assets), never revenue.
  const latest = financials[0] ?? null;
  const netAssets = latest?.equity ?? metrics?.netAssets ?? null;
  const totalAssets = latest?.totalAssets ?? ukEnrichment?.totalAssets ?? null;
  const equityRatio =
    netAssets != null && totalAssets != null && totalAssets > 0 ? netAssets / totalAssets : null;
  const balance: { value: string | null; chip: string | null; tone: ChipTone; why: string } =
    equityRatio == null
      ? metrics?.netAssets != null
        ? {
            value: `${money(metrics.netAssets)} net assets`,
            chip: metrics.netAssets < 0 ? 'negative equity' : null,
            tone: metrics.netAssets < 0 ? 'neg' : 'neutral',
            why: 'net assets on file · full balance sheet not itemised',
          }
        : { value: null, chip: null, tone: 'neutral', why: 'no balance sheet on file' }
      : equityRatio < 0
        ? {
            value: `${Math.round(equityRatio * 100)}% equity`,
            chip: 'negative equity',
            tone: 'neg',
            why: 'liabilities exceed assets',
          }
        : equityRatio < 0.2
          ? {
              value: `${Math.round(equityRatio * 100)}% equity`,
              chip: 'geared',
              tone: 'est',
              why: 'net assets ÷ total assets',
            }
          : equityRatio >= 0.5
            ? {
                value: `${Math.round(equityRatio * 100)}% equity`,
                chip: 'well-capitalised',
                tone: 'pos',
                why: 'net assets ÷ total assets',
              }
            : {
                value: `${Math.round(equityRatio * 100)}% equity`,
                chip: null,
                tone: 'neutral',
                why: 'net assets ÷ total assets',
              };

  // 5 · Trajectory — reported-revenue CAGR over ≥2 filed years (the financials
  // array is filed accounts, never modelled). Stable is a fired neutral state.
  const revYears = financials
    .filter((f) => f.revenue != null && f.revenue > 0)
    .map((f) => ({ year: f.periodEndDate, rev: f.revenue as number }))
    .sort((a, b) => a.year.localeCompare(b.year));
  const trajectory: { state: string; tone: ChipTone | null; why: string } = (() => {
    if (revYears.length < 2) return { state: 'n/a', tone: null, why: '<2 filed years' };
    const first = revYears[0]!;
    const last = revYears[revYears.length - 1]!;
    const yrs = Math.max(1, new Date(last.year).getFullYear() - new Date(first.year).getFullYear());
    const cagr = Math.pow(last.rev / first.rev, 1 / yrs) - 1;
    const p = Math.round(cagr * 100);
    const why = `filed revenue ${p >= 0 ? '+' : ''}${p}%/yr · ${revYears.length} yrs`;
    if (cagr > 0.05) return { state: 'Growing', tone: 'pos', why };
    if (cagr < -0.05) return { state: 'Declining', tone: 'neg', why };
    return { state: 'Stable', tone: 'neutral', why };
  })();

  // Service-quality trajectory — the temporal trend of regulator inspection
  // ratings (enm.quality_direction), distinct from the revenue trajectory above.
  // SERVE GATE (2026-07-22): the trajectory is the DERIVATIVE of the quality
  // tier — never show it without the tier itself. The stored value is computed
  // from raw CQC rating history while the tier comes from the calibrated
  // quality serve, so ~700 GB entities carry a direction with no served tier;
  // a "Stable" next to a "—" reads as data we don't have. The stored value
  // stays (it is a legitimate Seller-Readiness input); it just doesn't serve
  // without its level.
  const tierServed = !!metrics?.qualityTier && metrics.qualityTier !== 'Not rated';
  const qualityTrajectory: { tone: ChipTone | null; text: string | null } =
    tierServed && metrics?.qualityDirection === 'Deteriorating'
      ? { tone: 'neg', text: 'Deteriorating' }
      : tierServed && metrics?.qualityDirection === 'Improving'
        ? { tone: 'pos', text: 'Improving' }
        : tierServed && metrics?.qualityDirection === 'Stable'
          ? { tone: 'neutral', text: 'Stable' }
          : { tone: null, text: null };

  return (
    <Section>
      {metrics?.originationSignal != null && (
        <SignalRow label="Corryk Origination Signal" value={`${metrics.originationSignal}/100`} />
      )}
      {metrics?.sellerReadiness != null && (
        <SignalRow
          label="Corryk Seller Readiness Score"
          value={`${metrics.sellerReadiness}/100`}
          chip={
            metrics.sellerReadinessConfidence === 'Indicative'
              ? { tone: 'neutral', text: 'indicative' }
              : null
          }
          rationale={metrics.sellerReadinessNote ?? undefined}
        />
      )}
      <SignalRow
        label="Succession"
        chip={succession.tone ? { tone: succession.tone, text: succession.state } : null}
        proxy={ageIsProxy}
        rationale={succession.why}
      />
      <SignalRow
        label="Balance-sheet structure"
        value={balance.value}
        chip={balance.chip ? { tone: balance.tone, text: balance.chip } : null}
        rationale={balance.why}
      />
      <SignalRow
        label="Revenue trajectory"
        chip={trajectory.tone ? { tone: trajectory.tone, text: trajectory.state } : null}
        rationale={trajectory.why}
      />
      <SignalRow
        label="Service quality trajectory"
        chip={
          qualityTrajectory.tone
            ? { tone: qualityTrajectory.tone, text: qualityTrajectory.text as string }
            : null
        }
        rationale={
          qualityTrajectory.text
            ? 'trend of regulator inspection ratings across the operator’s sites'
            : tierServed
              ? 'no rating trajectory (fewer than 2 dated inspections on file)'
              : 'no served quality tier — trajectory withheld without its level'
        }
      />
      <EventsBlock entityId={entity.id} />
    </Section>
  );
}

// --- Company events -----------------------------------------------------
// The corporate_events feed (/entities/:id/activity): gazettes, Registro Mercantil
// acts, PSC change of control, public grants and contract awards. Facts only —
// every row links to its publisher. Newest first; the first few shown, rest on demand.

const EVENT_GROUP: Record<string, string> = {
  public_grant: 'grants',
  public_contract: 'public contracts',
  control_transfer: 'control changes',
  control_entry: 'control changes',
  control_exit: 'control changes',
  control_vacated: 'control changes',
  director_change: 'board changes',
  accounts_filed: 'accounts filed',
};
const EVENT_TONE: Record<string, ChipTone> = {
  bankruptcy: 'alert',
  liquidation: 'alert',
  dissolution: 'alert',
  control_transfer: 'est',
  ownership_change: 'est',
  merger: 'est',
  demerger: 'est',
};
const EVENTS_SHOWN = 8;

function EventsBlock({ entityId }: { entityId: string }) {
  const { data, isLoading } = useEntityActivity(entityId);
  const [open, setOpen] = useState(false);
  if (isLoading) return null;
  const rows = data?.data ?? [];
  if (rows.length === 0) return null;
  const groups = new Map<string, number>();
  for (const r of rows) {
    const g = EVENT_GROUP[r.eventType] ?? 'registry & other';
    groups.set(g, (groups.get(g) ?? 0) + 1);
  }
  const shown = open ? rows : rows.slice(0, EVENTS_SHOWN);
  return (
    <div style={{ marginTop: 'var(--s-3)' }}>
      <div
        style={{
          fontSize: 'var(--t-meta)',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--text-tertiary)',
          marginBottom: 2,
        }}
      >
        Company events
      </div>
      <Meta>
        {[...groups.entries()]
          .sort((a, b) => b[1] - a[1])
          .map(([g, n]) => `${formatCount(n)} ${g}`)
          .join(' · ')}
        {rows.length >= 200 ? ' (latest 200)' : ''}
      </Meta>
      {shown.map((r) => {
        const tone = EVENT_TONE[r.eventType];
        return (
          <div
            key={r.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '64px 1fr',
              gap: 'var(--s-2)',
              padding: '5px 0',
              borderBottom: '1px solid var(--hairline)',
              fontSize: 'var(--t-body)',
              alignItems: 'baseline',
            }}
          >
            <Mono style={{ color: 'var(--text-tertiary)' }}>{monthYear(r.effectiveDate)}</Mono>
            <span style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
              {tone && <Chip tone={tone}>{r.eventType.replace(/_/g, ' ')}</Chip>}
              {r.sourceUrl ? (
                <a
                  href={r.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: 'inherit', textDecoration: 'none' }}
                  title="Open the published source"
                >
                  {r.headline ?? r.eventType.replace(/_/g, ' ')}
                </a>
              ) : (
                <span>{r.headline ?? r.eventType.replace(/_/g, ' ')}</span>
              )}
            </span>
          </div>
        );
      })}
      {rows.length > EVENTS_SHOWN && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={{
            marginTop: 4,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            font: 'inherit',
            color: 'var(--text-secondary)',
          }}
        >
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          {open ? 'Show fewer' : `Show all ${formatCount(rows.length)}`}
        </button>
      )}
    </div>
  );
}

// One signal row: label · value/chip (or em-dash) · one concise rationale line.
function SignalRow({
  label,
  value,
  chip,
  proxy,
  rationale,
}: {
  label: string;
  value?: string | null;
  chip?: { tone: ChipTone; text: string } | null;
  proxy?: boolean;
  rationale?: string;
}) {
  const hasValue = value != null || chip != null;
  return (
    <div style={{ padding: '5px 0', borderBottom: '1px solid var(--hairline)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 'var(--s-3)',
          fontSize: 'var(--t-body)',
        }}
      >
        <span style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{label}</span>
        <span
          style={{
            textAlign: 'right',
            fontWeight: 600,
            display: 'flex',
            gap: 6,
            alignItems: 'baseline',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          {chip != null && <Chip tone={chip.tone}>{chip.text}</Chip>}
          {proxy && (
            <span
              style={{ fontSize: 'var(--t-meta)', fontWeight: 500, color: 'var(--text-tertiary)' }}
            >
              proxy
            </span>
          )}
          {value != null && <span>{value}</span>}
          {!hasValue && <Dash />}
        </span>
      </div>
      {rationale && (
        <div style={{ fontSize: 'var(--t-meta)', color: 'var(--text-tertiary)', marginTop: 2 }}>
          {rationale}
        </div>
      )}
    </div>
  );
}

// --- shared primitives ------------------------------------------------

function Section({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', flexDirection: 'column' }}>{children}</div>;
}

function Row({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 'var(--s-3)',
        padding: '5px 0',
        borderBottom: '1px solid var(--hairline)',
        fontSize: 'var(--t-body)',
      }}
    >
      <span style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{label}</span>
      <span
        style={{
          textAlign: 'right',
          fontWeight: 600,
          display: 'flex',
          gap: 6,
          alignItems: 'baseline',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        {children}
      </span>
    </div>
  );
}

function Dash() {
  return <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>—</span>;
}

function text(v: string | null | undefined) {
  return v ? <span>{v}</span> : <Dash />;
}

function Meta({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ fontWeight: 400, color: 'var(--text-tertiary)', fontSize: 'var(--t-meta)' }}>
      {children}
    </span>
  );
}

function Mono({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <span style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', ...style }}>
      {children}
    </span>
  );
}

type ChipTone = 'pos' | 'neg' | 'est' | 'info' | 'neutral' | 'alert';

function Chip({ tone, children }: { tone: ChipTone; children: React.ReactNode }) {
  // Concrete colours — the --color-*-100/300/700 ramps aren't defined in the
  // token system (only --positive/--accent/--alert + the -500 aliases), so
  // referencing them collapsed the chip to bare text. These match the mockup.
  const palette: Record<ChipTone, { bg: string; fg: string; border: string }> = {
    pos: { bg: 'var(--positive-wash, #e4f0ea)', fg: 'var(--positive, #0a7d5a)', border: '#b7d4af' },
    // Data-direction down (declining/below) — distinct from `alert` (errors).
    neg: { bg: 'var(--negative-wash, #f7e3e8)', fg: 'var(--negative, #c0143c)', border: '#e3b8c2' },
    est: { bg: '#f4e3c4', fg: 'var(--accent-hover, #8f4822)', border: '#e0c79a' },
    info: {
      bg: 'var(--surface-elevated, #e9d4b6)',
      fg: 'var(--accent, #a8572a)',
      border: '#d8c19c',
    },
    neutral: { bg: 'var(--menu-surface)', fg: 'var(--text-primary)', border: 'var(--hairline)' },
    alert: { bg: '#f6dada', fg: 'var(--alert, #d1242f)', border: '#e3b3b3' },
  };
  const p = palette[tone];
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 'var(--t-meta)',
        padding: '1px 6px',
        border: `1px solid ${p.border}`,
        borderRadius: 3,
        background: p.bg,
        color: p.fg,
        fontWeight: 600,
      }}
    >
      {children}
    </span>
  );
}

function actBtnStyle(on: boolean): React.CSSProperties {
  return {
    height: 22,
    border: `1px solid ${on ? 'var(--color-accent-300)' : 'var(--hairline)'}`,
    background: on ? 'var(--color-accent-100)' : 'var(--menu-surface)',
    color: on ? 'var(--accent)' : 'var(--text-secondary)',
    borderRadius: 4,
    fontSize: 'var(--t-meta)',
    fontWeight: 600,
    padding: '0 8px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
  };
}

function histTh(align: 'left' | 'right'): React.CSSProperties {
  return {
    textAlign: align,
    padding: '3px 4px',
    fontSize: 'var(--t-meta)',
    color: 'var(--text-tertiary)',
    fontWeight: 600,
    borderBottom: '1px solid var(--hairline)',
  };
}

function histTd(align: 'left' | 'right'): React.CSSProperties {
  return {
    textAlign: align,
    padding: '3px 4px',
    fontWeight: align === 'left' ? 400 : 600,
    color: align === 'left' ? 'var(--text-secondary)' : 'var(--text-primary)',
    fontVariantNumeric: 'tabular-nums',
  };
}

// --- helpers ----------------------------------------------------------

function vintageYear(data: CompanyDetailResponse): string | null {
  const d = data.ukEnrichment?.accountsMadeUpTo ?? data.financials[0]?.periodEndDate ?? null;
  if (!d) return null;
  const y = new Date(d).getFullYear();
  return Number.isFinite(y) ? `FY ${y}` : null;
}

// Company type. Prefer the stored legal_form; for FR it's a numeric INSEE
// "catégorie juridique" code, which we map to a readable form. When legal_form
// is absent (GB entities don't carry it today) derive it from the legal-name
// suffix — Companies House requires the suffix to match the company type, so
// "… LIMITED" is reliably a private limited company.
function formatCompanyType(entity: CompanyEntity): string | null {
  const lf = entity.legalForm?.trim();
  if (lf) {
    if (entity.countryCode === 'FR' && /^\d+$/.test(lf)) return inseeLegalForm(lf);
    return lf;
  }
  return deriveTypeFromName(entity.legalName);
}

// French INSEE "catégorie juridique" codes → readable legal forms. Not
// exhaustive (there are hundreds); covers the forms that actually turn up among
// French healthcare companies, with a family fallback (by leading digits) for
// the rest and the raw code as a last resort rather than a wrong guess.
const INSEE_LEGAL_FORMS: Record<string, string> = {
  '1000': 'Entrepreneur individuel',
  '5202': 'SNC',
  '5306': 'Société en commandite simple',
  '5308': 'Société en commandite par actions',
  '5385': 'SELCA',
  '5485': 'SELARL',
  '5499': 'SARL',
  '5510': 'SA',
  '5585': 'SELAFA',
  '5599': 'SA',
  '5610': 'SA (directoire)',
  '5685': 'SELAFA',
  '5699': 'SA (directoire)',
  '5710': 'SAS',
  '5720': 'SASU',
  '5785': 'SELAS',
  '5800': 'Société européenne',
  '6220': 'GIE',
  '6540': 'SCI',
  '6541': 'SCI (construction-vente)',
  '6560': 'Société civile',
  '6561': 'SCM',
  '6562': 'SCP',
  '6589': 'Société civile',
  '6901': 'Autre personne morale de droit privé',
  '9220': 'Association déclarée',
};

function inseeLegalForm(code: string): string {
  const hit = INSEE_LEGAL_FORMS[code];
  if (hit) return hit;
  if (code.startsWith('54')) return 'SARL';
  if (code.startsWith('55') || code.startsWith('56')) return 'SA';
  if (code.startsWith('57')) return 'SAS';
  if (code.startsWith('65')) return 'Société civile';
  if (code.startsWith('10')) return 'Entrepreneur individuel';
  if (code.startsWith('92')) return 'Association';
  return code;
}

function deriveTypeFromName(name: string | null): string | null {
  if (!name) return null;
  const n = name
    .trim()
    .toUpperCase()
    .replace(/[.,]+$/, '');
  if (/(PUBLIC LIMITED COMPANY|\bP\.?L\.?C\.?)$/.test(n)) return 'Public limited company';
  if (/(COMMUNITY INTEREST COMPANY|\bC\.?I\.?C\.?)$/.test(n)) return 'Community interest company';
  if (/(LIMITED LIABILITY PARTNERSHIP|\bLLP)$/.test(n)) return 'Limited liability partnership';
  if (/LIMITED PARTNERSHIP$/.test(n)) return 'Limited partnership';
  if (/(LIMITED|\bLTD)$/.test(n)) return 'Private limited company';
  return null;
}

function monthYear(d: string): string {
  const dt = new Date(d);
  return Number.isFinite(dt.getTime())
    ? dt.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
    : d;
}
